import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useTabState } from '../../hooks/useTabState';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import Button from '../../components/Button';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import NoInfoMessage from '../../components/NoInfoMessage';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import ReinstateVisitorsModal from './ReinstateVisitorsModal';
import DisplayDate from '../../components/DisplayDate';

// TYPES
import { VisitorsDashboardVisitor } from '../../api/models';
type TabStatus = 'current' | 'past';

const Visitors: React.FC = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to hold fetch data
	const [visitorsData, setVisitorsData] = React.useState<VisitorsDashboardVisitor[]>([]);

	// State to control active tab and content
	const [activeTab, setActiveTab] = useTabState<TabStatus>('current', 'tabView');

	const [reinstateVisitorId, setReinstateVisitorId] = React.useState<string>('');

	const [showReinstateVisitorsModal, setShowReinstateVisitorsModal] = React.useState<boolean>(false);

	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	const getVisitorsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getVisitorsData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setVisitorsData(result);
		}
	};

	React.useEffect(() => {
		getVisitorsData();
	}, []);

	const currentVisitorsData = visitorsData.filter((item) => item.isCurrentMember === true);
	const pastVisitorsData = visitorsData.filter((item) => item.isCurrentMember === false);

	return (
		<>
			<div className="page-general-layout">
				{!!formErrorMessage ? (
					<div className="form-validation-error">{formErrorMessage}</div>
				) : (
					<SwitchTransition mode="out-in">
						<CSSTransition
							key={`key_${isLoading}`}
							timeout={600}
							classNames="standalone-fade"
							mountOnEnter
							unmountOnExit
						>
							{isLoading ? (
								<LoadingSpinner show={isLoading} />
							) : (
								<>
									<nav className="page-header-section">
										<div className="header-left">
											<Button
												onClick={() => setActiveTab('current')}
												className={`staff-category pdl-0 ${activeTab === 'current' ? 'active' : ''}`}
											>
												Current
											</Button>

											<Button
												onClick={() => setActiveTab('past')}
												className={`staff-category ${activeTab === 'past' ? 'active' : ''}`}
											>
												Past
											</Button>
										</div>
									</nav>
									{isOrganisationAdmin && (currentVisitorsData.length > 0 || pastVisitorsData.length > 0) ? (
										<section className="info-section mtop-40 mbtom-30">
											{activeTab === 'current' ? (
												<ul>
													{currentVisitorsData.map((item) => {
														return (
															<li key={item.id} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0">
																		<CircleImage
																			src={item?.profileImage}
																			firstName={item?.firstName}
																			lastName={item?.lastName}
																			isProfileInComplete={item.needsAttention}
																		/>
																	</div>

																	<div className="col-md-auto full-width">
																		<div className="row detail-info">
																			<div className="col-lg-4 detail-name">
																				{item.firstName} {item.lastName}
																			</div>
																			{item.visitPurpose && (
																				<div className="col-lg-4 detail-status">
																					<div className="title">{item.visitPurpose}</div>

																					<div className="name color-grey-4">{item.businessName}</div>
																				</div>
																			)}
																		</div>
																	</div>

																	<div className="col-auto pdr-0 link-group">
																		<Link className="btn-text pdr-0" to={`visitors/${item?.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</li>
														);
													})}
												</ul>
											) : activeTab === 'past' ? (
												<ul>
													{pastVisitorsData.map((item) => {
														return (
															<li key={item.id} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0">
																		<CircleImage
																			src={item?.profileImage}
																			firstName={item?.firstName}
																			lastName={item?.lastName}
																			isProfileInComplete={item.needsAttention}
																		/>
																	</div>

																	<div className="col-md-auto full-width">
																		<div className="row detail-info">
																			<div className="col-lg-3">
																				{item.firstName} {item.lastName}
																			</div>

																			{item?.leftDate && (
																				<>
																					<div className="col-lg-auto">
																						<span className="body-small text-grey-4 fw-bold mr-1">Start</span>

																						<span className="fw-medium body-small text-grey-4">
																							<DisplayDate>{item?.joinedDate}</DisplayDate>
																						</span>
																					</div>

																					<div className="col-lg-3">
																						<span className="body-small text-grey-4 fw-bold mr-1">End</span>

																						<span className="fw-medium body-small text-grey-4">
																							<DisplayDate>{item?.leftDate}</DisplayDate>
																						</span>
																					</div>
																				</>
																			)}
																		</div>
																	</div>

																	<div className="col-auto pdr-0 link-group">
																		<Button
																			type="button"
																			className="btn-text dark-text"
																			onClick={() => {
																				setReinstateVisitorId(item.id || '');
																				setShowReinstateVisitorsModal(true);
																			}}
																		>
																			Reinstate
																		</Button>
																	</div>

																	<div className="col-auto pdr-0 link-group">
																		<Link className="btn-text pdr-0" to={`past-visitors/${item?.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</li>
														);
													})}
												</ul>
											) : null}
										</section>
									) : (
										<NoInfoMessage message="There are no visitors associated with your organisation." />
									)}
								</>
							)}
						</CSSTransition>
					</SwitchTransition>
				)}
			</div>
			<GlobalModal
				isOpen={showReinstateVisitorsModal}
				setIsOpen={setShowReinstateVisitorsModal}
				titleText="Reinstate Visitor"
			>
				<ReinstateVisitorsModal
					reinstateVisitorId={reinstateVisitorId}
					closeModal={() => setShowReinstateVisitorsModal(false)}
				/>
			</GlobalModal>
		</>
	);
};

export default Visitors;
